import { getQueryObjectFromObject as d } from "@partoohub/utils";
import { ApiResourceBaseClass as S } from "./baseApiCalls.js";
const o = "conversations", i = "messages", h = `${o}/stats`;
class R extends S {
  /**
   * Fetches a conversation.
   *
   * HTTP Verb: `GET`
   * Endpoint URL: `/api/v2/conversations/{conversation_id}`
   *
   * @param {number} [conversationId] - Id of the conversation.
   * @returns {Promise<Conversation>} A promise that resolves to the conversation.
   */
  getConversation = async (t) => {
    const { data: a } = await this.get(
      `${o}/${t}`
    );
    return a;
  };
  /**
   * Lists conversations.
   *
   * HTTP Verb: `GET`
   * Endpoint URL: `/api/v2/conversations`
   *
   * @param {SearchBusinessParams} [queryFilter] - Business search filters.
   * @param {string | null} [cursor] - Cursor used for pagination.
   * @param {Lowercase<ConversationStatus>} [status] - Filter on the conversation's status.
   * @param {string | null} [assignedTo] - Filter on the conversation's assignation status.
   * Provide the user_id of the assigned user, of one of the following special values: ["unassigned", "automated", "lead_collected"]
   * @returns {Promise<ConversationsData>} A promise that resolves to a list of conversations, cursor paginated.
   */
  getConversations = async (t = {}, a = null, s, e) => {
    const n = {
      ...d(t),
      cursor: a,
      status: s,
      assigned_to: e
    }, { data: c } = await this.get(`${o}`, n);
    return c;
  };
  /**
   * Marks a conversation as read for the user.
   *
   * HTTP Verb: `POST`
   * Endpoint URL: `/api/v2/conversations/{conversation_id}/read`
   *
   * @param {number} [conversationId] - Id of the conversation.
   * @returns {Promise<SuccessResponse>} A promise that resolves to a success payload.
   */
  setConversationRead = async (t) => {
    const { data: a } = await this.post(
      `${o}/${t}/read?partoo_ui=true`
    );
    return a;
  };
  /**
   * Sends a message from the user to a conversation
   *
   * HTTP Verb: `POST`
   * Endpoint URL: `/api/v2/messages`
   *
   * @param {number} [conversationId] - Id of the conversation.
   * @param {string | File} [message] - Content of the message (string for Text, File for Tmage)
   * @param {string} [date] - Creation date of the message (ex: "2025-03-11 13:11:13.371000+00:00")
   * @param {MessageContentType} [contentType] - ContentType of the message (Text or Image).
   * @param {string | null} [templateId] - Id of the template used to create the message.
   * @returns {Promise<SuccessResponseWithMessageId>} A promise that resolves to a success payload, containing the id of the message created.
   */
  postMessage = async (t, a, s, e, n, c = !1) => {
    if (e === "Text" || c) {
      const _ = `${i}?partoo_ui=true`, y = {
        conversation_id: t,
        content_type: e,
        content: a,
        date: s,
        ...n && { template_id: n }
      }, { data: m } = await this.post(_, y);
      return m;
    }
    const u = `${i}?partoo_ui=true`, p = { "Content-Type": "multipart/form-data" }, r = new FormData();
    r.append("conversation_id", t.toString()), r.append("content_type", "Image"), r.append("image", a), r.append("date", s);
    const { data: g } = await this.post(u, r, p);
    return g;
  };
  /**
   * Lists the messages of a given conversations.
   *
   * HTTP Verb: `GET`
   * Endpoint URL: `/api/v2/messages`
   *
   * @param {number} [conversationId] - Id of the conversation.
   * @param {string | null} [cursor] - Cursor used for pagination.
   * @returns {Promise<ConversationMessagesData>} A promise that resolves to a list of messages, cursor paginated.
   */
  getMessages = async (t, a = null) => {
    const s = {
      conversation_id: t,
      cursor: a,
      with_metadata: !0,
      partoo_ui: !0
    }, { data: e } = await this.get(i, s);
    return e;
  };
  /**
   * Retries to sends a User message, with status Failed.
   *
   * HTTP Verb: `POST`
   * Endpoint URL: `/api/v2/messages/{message_id}/retry`
   *
   * @param {number} [messageId] - Id of the message.
   * @returns {Promise<SuccessResponse>} A promise that resolves to a success payload.
   */
  retryToSendMessage = async (t) => {
    const a = `${i}/${t}/retry`, s = { message_id: t }, { data: e } = await this.post(a, s);
    return e;
  };
  /**
   * Fetches the overall conversations stats.
   *
   * HTTP Verb: `GET`
   * Endpoint URL: `/api/v2/conversations/stats`
   *
   * @param {SearchBusinessParams} [queryFilter] - Business search filters.
   * @returns {Promise<ConversationStats>} A promise that resolves to a stat object.
   */
  getConversationsStats = async (t = {}) => {
    const a = {
      partoo_ui: !0,
      ...d(t)
    }, { data: s } = await this.get(
      h,
      a
    );
    return s;
  };
  /**
   * Fetches infos on banners/modals/buttons to display conditionally in the interface.
   *
   * HTTP Verb: `GET`
   * Endpoint URL: `/api/v2/messaging/infos`
   *
   * @returns {Promise<MessagingInfos>} A promise that resolves to an object containing different flags.
   */
  getInfos = async () => {
    const { data: t } = await this.get("messaging/infos");
    return t;
  };
  /**
   * Updates the status of the given conversation.
   *
   * HTTP Verb: `PATCH`
   * Endpoint URL: `/api/v2/conversations/{conversation_id}`
   *
   * @param {number} [conversationId] - Id of the conversation.
   * @param {Lowercase<ConversationStatus>} [status] - New status of the conversation.
   * @returns {Promise<SuccessResponseWithMessageId>} A promise that resolves to a success payload, containing the id of the opening/closing message.
   */
  updateConversationStatus = async (t, a) => {
    const s = `${o}/${t}`, e = { status: a }, { data: n } = await this.patch(s, e);
    return n;
  };
  /**
   * Updates the assigned user of a given conversation.
   *
   * HTTP Verb: `PATCH`
   * Endpoint URL: `/api/v2/conversations/{conversation_id}`
   *
   * @param {number} [conversationId] - Id of the conversation.
   * @param {string} [assignedUserId] - Id of the newly assigned user or "unassigned" to unassign.
   * @returns {Promise<SuccessResponseWithMessageId>} A promise that resolves to a success payload, containing the id of the opening/closing message.
   */
  updateConversationAssignedUser = async (t, a) => {
    const s = { assigned_user_id: a }, { data: e } = await this.patch(
      `${o}/${t}`,
      s
    );
    return e;
  };
}
export {
  R as Messaging,
  R as default
};
